import { useMemo } from 'react';

import {
  GeneratedGetArtistsByIdsQuery,
  GeneratedSubscriptionsQuery,
  GeneratedSubscriptionType,
} from '../../types/generated';

export type SubscriptionItem =
  GeneratedSubscriptionsQuery['subscriptions']['subscriptions'][number];

export type Artist = GeneratedGetArtistsByIdsQuery['artists'][number];

/**
 * A subscription enriched server-side with the internal artist ids it belongs
 * to. For ARTIST subs that's `[objectId]`; for PRODUCT/RELEASE it's the result
 * of classifying `objectId` through the product/release → artist maps.
 */
export interface AnnotatedSubscriptionItem extends SubscriptionItem {
  artistIds: string[];
}

/**
 * One of the subscriptions belonging to an artist group. Carries everything
 * needed to re-create it after an unfollow (`type`/`objectId`/`name`/`ref`)
 * plus its full artist set for the shared-collab rule.
 */
export interface SubscriptionMember {
  subscriptionId: string;
  type: GeneratedSubscriptionType;
  objectId: string;
  name: string;
  ref: string;
  artistIds: string[];
}

export interface ArtistSubscriptionGroup {
  artist: {
    id: string;
    name: string;
    thumbnail: string | null;
  };
  members: SubscriptionMember[];
}

export interface UseMyArtistSubscriptionsArgs {
  subscriptions: AnnotatedSubscriptionItem[];
  artists: Artist[];
}

/**
 * Groups the user's subscriptions (of any type) by artist id. A subscription
 * shared by multiple artists (a collaboration) joins every matching group.
 */
export const useMyArtistSubscriptions = ({
  artists,
  subscriptions,
}: UseMyArtistSubscriptionsArgs): ArtistSubscriptionGroup[] => {
  const artistsById = useMemo(() => {
    const map = new Map<string, Artist>();
    artists.forEach((a) => map.set(a.id, a));
    return map;
  }, [artists]);

  return useMemo(() => {
    const groups = new Map<string, ArtistSubscriptionGroup>();

    subscriptions.forEach((s) => {
      const member: SubscriptionMember = {
        subscriptionId: s.id,
        type: s.type,
        objectId: s.objectId,
        name: s.name,
        ref: s.ref,
        artistIds: s.artistIds,
      };

      s.artistIds.forEach((artistId) => {
        const artist = artistsById.get(artistId);
        if (!artist) return;

        let group = groups.get(artistId);
        if (!group) {
          group = {
            artist: {
              id: artist.id,
              name: artist.name,
              thumbnail: artist.thumbnail ?? null,
            },
            members: [],
          };
          groups.set(artistId, group);
        }
        group.members.push(member);
      });
    });

    // Only surface artists the user actually follows
    return Array.from(groups.values()).filter((group) =>
      group.members.some(
        (m) =>
          m.type === GeneratedSubscriptionType.Artist &&
          m.objectId === group.artist.id,
      ),
    );
  }, [subscriptions, artistsById]);
};
